import React from "react";
import { Group, Rect, Text } from "react-konva";
import { IMondayTaskController } from "../../controller";
import {
  MondayTaskSection,
  MONDAY_TASK_WIDTH,
  MONDAY_TASK_DYNAMIC_SECTION_PADDING,
  MONDAY_TASK_UPDATES_HEADER_HEIGHT,
} from "shared/datamodel/monday-task";
import { FONT_SIZE, FONT_FAMILY, PADDING } from "../../consts";
import useObservableController from "elements/hooks/use-observable-controller";
import { ShowMoreButton } from "./show-more-button";
import { UpdateRow } from "./update-row";

interface UpdatesProps {
  controller: IMondayTaskController;
}

const HEADER_HEIGHT = MONDAY_TASK_UPDATES_HEADER_HEIGHT;
const TEXT_COLOR = "#1E1E1E";

export const Updates = React.memo(({ controller }: UpdatesProps) => {
  useObservableController(controller, [
    "updates-visibility-changed",
    "update-expansion-changed",
    "size-changed",
    "item-changed",
  ]);

  const showAllUpdates = controller.element.showAllUpdates ?? false;
  const y = controller.getSectionY(MondayTaskSection.UPDATES);
  const updates = controller.displayedUpdates();
  const hasMoreUpdates = controller.hasMoreUpdates();
  const updateYPositions = controller.getUpdateYPositions();
  const showMoreY = controller.getShowMoreY();

  // Don't render if no updates
  if (updates.length === 0) {
    return null;
  }

  const handleToggleUpdates = (e: { evt: Event }) => {
    e.evt.stopPropagation();
    e.evt.preventDefault();
    controller.toggleShowAllUpdates();
  };

  return (
    <Group y={y}>
      <Rect width={MONDAY_TASK_WIDTH} height={1} fill="#EBEDF3" />

      <Group x={PADDING} y={MONDAY_TASK_DYNAMIC_SECTION_PADDING}>
        <Text
          text="Updates"
          fill={TEXT_COLOR}
          fontFamily={FONT_FAMILY}
          fontSize={FONT_SIZE}
          fontStyle="600"
          letterSpacing={0.1871}
        />

        <Group y={HEADER_HEIGHT}>
          {updates.map((update, index) => {
            const isExpanded = controller.element.expandedUpdateId === update.id;

            return (
              <UpdateRow
                key={update.id}
                controller={controller}
                update={update}
                isExpanded={isExpanded}
                y={updateYPositions[index]}
              />
            );
          })}
        </Group>

        {hasMoreUpdates && (
          <ShowMoreButton x={180} y={showMoreY} showAllUpdates={showAllUpdates} onToggle={handleToggleUpdates} />
        )}
      </Group>
    </Group>
  );
});

Updates.displayName = "Updates";
